<template>
    <Head title="Work Orders Calendar" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="mb-6">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-white">Work Orders Calendar</h1>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        View and manage scheduled work orders in calendar format
                    </p>
                </div>
                <div class="mt-4 sm:mt-0">
                    <Link
                        :href="route('work-orders.index')"
                        class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        List View
                    </Link>
                </div>
            </div>
        </div>

        <!-- Filters and Controls -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <!-- View Type Filter -->
                    <div class="flex items-center space-x-2">
                        <label class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">View:</label>
                        <select
                            v-model="viewType"
                            @change="handleViewChange"
                            class="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">All Work Orders</option>
                            <option value="my">My Work Orders</option>
                            <option v-if="canViewTeam" value="team">Team Work Orders</option>
                        </select>
                    </div>

                    <!-- Technician Filter -->
                    <div v-if="viewType === 'all' || viewType === 'team'" class="flex items-center space-x-2">
                        <label class="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-300">Technician:</label>
                        <select
                            v-model="selectedTechnician"
                            @change="handleTechnicianChange"
                            class="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Technicians</option>
                            <option v-for="tech in technicians" :key="tech.id" :value="tech.id">
                                {{ tech.name }}
                            </option>
                        </select>
                    </div>

                    <!-- Calendar View Buttons -->
                    <div class="flex items-center space-x-2">
                        <div class="inline-flex rounded-md shadow-sm">
                            <button
                                @click="changeCalendarView('dayGridMonth')"
                                :class="[
                                    'px-4 py-2 text-sm font-medium rounded-l-md border',
                                    calendarView === 'dayGridMonth'
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                ]"
                            >
                                Month
                            </button>
                            <button
                                @click="changeCalendarView('timeGridWeek')"
                                :class="[
                                    'px-4 py-2 text-sm font-medium border-t border-b',
                                    calendarView === 'timeGridWeek'
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                ]"
                            >
                                Week
                            </button>
                            <button
                                @click="changeCalendarView('timeGridDay')"
                                :class="[
                                    'px-4 py-2 text-sm font-medium border-t border-b',
                                    calendarView === 'timeGridDay'
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                ]"
                            >
                                Day
                            </button>
                            <button
                                @click="changeCalendarView('listWeek')"
                                :class="[
                                    'px-4 py-2 text-sm font-medium rounded-r-md border',
                                    calendarView === 'listWeek'
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                ]"
                            >
                                List
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Legend -->
            <div class="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <!-- Status Legend -->
                    <div>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Status</p>
                        <div class="flex flex-wrap gap-2">
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                <span class="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>
                                Open
                            </span>
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                <span class="w-2 h-2 rounded-full bg-yellow-500 mr-1"></span>
                                In Progress
                            </span>
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                <span class="w-2 h-2 rounded-full bg-purple-500 mr-1"></span>
                                Review
                            </span>
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                <span class="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                                Completed
                            </span>
                        </div>
                    </div>

                    <!-- Priority Legend -->
                    <div>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Priority (Border)</p>
                        <div class="flex flex-wrap gap-2">
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white dark:bg-gray-800 text-green-700 dark:text-green-400 border-2 border-green-500">
                                Low
                            </span>
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-400 border-2 border-blue-500">
                                Normal
                            </span>
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white dark:bg-gray-800 text-orange-700 dark:text-orange-400 border-2 border-orange-500">
                                High
                            </span>
                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white dark:bg-gray-800 text-red-700 dark:text-red-400 border-2 border-red-600">
                                Urgent
                            </span>
                        </div>
                    </div>

                    <!-- Stats -->
                    <div>
                        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Summary</p>
                        <div class="text-sm text-gray-700 dark:text-gray-300">
                            <p>Total Events: <span class="font-semibold">{{ filteredEvents.length }}</span></p>
                            <p v-if="viewType === 'my'">Your scheduled work orders</p>
                            <p v-else-if="viewType === 'team'">Team's scheduled work orders</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Calendar -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div class="p-4">
                <FullCalendar
                    ref="fullCalendar"
                    :options="calendarOptions"
                    class="work-orders-calendar"
                />
            </div>
        </div>

    </div>
</template>

<script setup>
// Import dependencies
import { ref, computed, onMounted } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';

const props = defineProps({
    events: {
        type: Array,
        required: true,
    },
    technicians: {
        type: Array,
        default: () => [],
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

// State
const fullCalendar = ref(null);
const viewType = ref('all');
const selectedTechnician = ref('');
const calendarView = ref('dayGridMonth');

// Page props
const page = usePage();

// Timezone for calendar display
const { timezone } = useTimezone();

// Permissions
const canViewTeam = computed(() => {
    return page.props.auth?.permissions?.includes('maintenance.work_orders.view_all') || false;
});

// Current user ID
const currentUserId = computed(() => {
    return page.props.auth?.user?.id || null;
});

// Filtered events based on view type and technician
const filteredEvents = computed(() => {
    let filtered = [...props.events];

    // Filter by view type
    if (viewType.value === 'my') {
        filtered = filtered.filter(event =>
            event.extendedProps?.assigned_to === currentUserId.value
        );
    } else if (viewType.value === 'team') {
        // Team view shows all work orders (you can customize this logic)
        // For now, showing all
    }

    // Filter by technician
    if (selectedTechnician.value) {
        filtered = filtered.filter(event =>
            event.extendedProps?.assigned_to === parseInt(selectedTechnician.value)
        );
    }

    return filtered;
});

// Calendar options
const calendarOptions = computed(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    initialView: calendarView.value,
    timeZone: timezone.value, // Use company timezone for calendar display
    headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: '', // We use custom buttons
    },
    events: filteredEvents.value,
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    weekends: true,
    height: 'auto',
    eventClick: handleEventClick,
    eventMouseEnter: handleEventMouseEnter,
    eventMouseLeave: handleEventMouseLeave,
    dateClick: handleDateClick,
    eventClassNames: 'cursor-pointer',
    eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        meridiem: 'short',
    },
    slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        meridiem: 'short',
    },
}));

// Methods
const changeCalendarView = (view) => {
    calendarView.value = view;
    if (fullCalendar.value) {
        fullCalendar.value.getApi().changeView(view);
    }
};

const handleViewChange = () => {
    // Reset technician filter when changing view type
    if (viewType.value === 'my') {
        selectedTechnician.value = '';
    }
};

const handleTechnicianChange = () => {
    // Filtering is handled by computed property
};

const handleEventClick = (info) => {
    info.jsEvent.preventDefault();

    // Navigate to work order show page
    if (info.event.extendedProps?.work_order_id) {
        router.visit(route('work-orders.show', info.event.extendedProps.work_order_id));
    }
};

const handleEventMouseEnter = (info) => {
    // Add hover effect
    info.el.style.opacity = '0.8';
    info.el.style.transform = 'scale(1.02)';
    info.el.style.transition = 'all 0.2s ease';

    // Show tooltip
    const tooltip = document.createElement('div');
    tooltip.className = 'fc-event-tooltip';
    tooltip.innerHTML = `
        <div class="bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg max-w-xs">
            <p class="font-semibold">${info.event.title}</p>
            ${info.event.extendedProps?.asset ? `<p class="mt-1">Asset: ${info.event.extendedProps.asset}</p>` : ''}
            ${info.event.extendedProps?.location ? `<p>Location: ${info.event.extendedProps.location}</p>` : ''}
            <p class="mt-1 text-xs opacity-75">Click to view details</p>
        </div>
    `;
    tooltip.style.position = 'absolute';
    tooltip.style.zIndex = '9999';
    tooltip.style.pointerEvents = 'none';
    document.body.appendChild(tooltip);

    const rect = info.el.getBoundingClientRect();
    tooltip.style.top = `${rect.top - tooltip.offsetHeight - 5}px`;
    tooltip.style.left = `${rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2)}px`;

    info.el.tooltip = tooltip;
};

const handleEventMouseLeave = (info) => {
    // Remove hover effect
    info.el.style.opacity = '1';
    info.el.style.transform = 'scale(1)';

    // Remove tooltip
    if (info.el.tooltip) {
        document.body.removeChild(info.el.tooltip);
        delete info.el.tooltip;
    }
};

const handleDateClick = (info) => {
    // Optional: Could navigate to create work order with this date pre-filled
    console.log('Date clicked:', info.dateStr);
};

// Lifecycle
onMounted(() => {
    // Any initialization needed
});
</script>

<style>
/* FullCalendar Custom Styles */
.work-orders-calendar {
    --fc-border-color: #e5e7eb;
    --fc-button-bg-color: #3b82f6;
    --fc-button-border-color: #3b82f6;
    --fc-button-hover-bg-color: #2563eb;
    --fc-button-hover-border-color: #2563eb;
    --fc-button-active-bg-color: #1d4ed8;
    --fc-button-active-border-color: #1d4ed8;
    --fc-today-bg-color: rgba(59, 130, 246, 0.1);
}

.dark .work-orders-calendar {
    --fc-border-color: #374151;
    --fc-page-bg-color: #1f2937;
    --fc-neutral-bg-color: #111827;
}

.work-orders-calendar .fc-event {
    border-width: 2px;
    border-radius: 4px;
    padding: 2px 4px;
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s ease;
}

.work-orders-calendar .fc-event:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.work-orders-calendar .fc-daygrid-event {
    white-space: normal;
}

.work-orders-calendar .fc-toolbar-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
}

.dark .work-orders-calendar .fc-toolbar-title {
    color: #f9fafb;
}

.work-orders-calendar .fc-col-header-cell {
    background-color: #f9fafb;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    padding: 0.75rem;
}

.dark .work-orders-calendar .fc-col-header-cell {
    background-color: #111827;
    color: #9ca3af;
}

.work-orders-calendar .fc-daygrid-day-number {
    font-weight: 500;
    color: #374151;
    padding: 0.5rem;
}

.dark .work-orders-calendar .fc-daygrid-day-number {
    color: #d1d5db;
}

.work-orders-calendar .fc-button {
    text-transform: capitalize;
    font-weight: 500;
    padding: 0.5rem 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .work-orders-calendar .fc-toolbar {
        flex-direction: column;
        gap: 0.5rem;
    }

    .work-orders-calendar .fc-toolbar-chunk {
        display: flex;
        justify-content: center;
    }
}
</style>
